import { BehaviorSubject, Observable } from 'rxjs';
import { InjectionToken } from '../../injection';

import { SerializableObject } from '../data/serializable-object';

import { ApiEndpointConfiguration } from './api-endpoint-configuration.interface';
import { IApiEndpointInformation } from './api-endpoint-information.interface';

/**
 * Type holding the possible return values for the ContentType header when downloading a file.
 * 'Generic' gets mapped to octet-stream, whereas 'Specific' to the actual file's type (e.g., PDF)
 */
export type ContentType = 'Generic' | 'Specific';

/**
 * Type holding the possible return values for the ContentDisposition header when downloading a file.
 */
export type ContentDisposition = 'Attachment' | 'Inline';

/**
 * NOTE: For API responses that return an Array, a `SerializableObjectList` instance should be
 *       provided to the HttpService. The data can then be mapped back to an array.
 *
 * TODO: Refactor IMobizHttpService methods to not require an SerializableObject instance.
 *
 * @see SerializableObject
 * @see SerializableObjectList
 * @see SerializableParams
 */
export interface IMobizHttpService {
  /**
   * This flag is emitted whenever the network connection status is changing.
   *
   * @deprecated use ConnectionStateService.STATE$
   */
  networkConnectionStatus: BehaviorSubject<boolean>;

  /**
   * Perform an API call to check the network connection status. False is returned for connection timeout,
   * DNS error, offline and other similar network errors.
   *
   * @param updateSubject optional flag for also updating the networkConnectionStatus when the request has finished.
   */
  checkNetworkConnectionStatus(updateSubject?: boolean): Observable<boolean>;

  /**
   * GET resource<T> from Mobiz.
   * @param instance The instance of object <T>.
   * @param config The Api endpoint configuration.
   *
   * @throws {UnknownNetworkError} An unknown network error has occurred.
   * @throws {MobizHttpErrorResponse} The API returned an error response.
   * @throws {SerializableObjectBuildError} Failed to build the response class.
   * @throws An unknown error has occurred, a very rare situation where the HTTP service has some problems.
   */
  get<T extends SerializableObject>(instance: T, config: ApiEndpointConfiguration<any>): Observable<T>;

  /**
   * POST resource<T> to Mobiz.
   * @param instance The instance of object <T>.
   * @param config The Api endpoint configuration.
   *
   * @throws {UnknownNetworkError} An unknown network error has occurred.
   * @throws {MobizHttpErrorResponse} The API returned an error response.
   * @throws {SerializableObjectBuildError} Failed to build the response class.
   * @throws An unknown error has occurred, a very rare situation where the HTTP service has some problems.
   */
  post<T extends SerializableObject>(instance: T, config: ApiEndpointConfiguration<any>): Observable<T>;

  /**
   * PUT resource<T> to Mobiz.
   * @param instance The instance of object <T>.
   * @param config The Api endpoint configuration.
   *
   * @throws {UnknownNetworkError} An unknown network error has occurred.
   * @throws {MobizHttpErrorResponse} The API returned an error response.
   * @throws {SerializableObjectBuildError} Failed to build the response class.
   * @throws An unknown error has occurred, a very rare situation.
   */
  put<T extends SerializableObject>(instance: T, config: ApiEndpointConfiguration<any>): Observable<T>;

  /**
   * DELETE resource<T> from Mobiz.
   *
   * To delete a resource that has not been loaded, you can use an `EmptyResponse` instance.
   *
   * @param instance The instance of object <T>.
   * @param config The Api endpoint configuration.
   *
   * @throws {UnknownNetworkError} An unknown network error has occurred.
   * @throws {MobizHttpErrorResponse} The API returned an error response.
   * @throws {SerializableObjectBuildError} Failed to build the response class.
   * @throws An unknown error has occurred, a very rare situation where the HTTP service has some problems.
   */
  delete<T extends SerializableObject>(instance: T, config: ApiEndpointConfiguration<any>): Observable<T>;

  /**
   * Returns the identification headers plus Content-Type and Accepts for application/json.
   *
   * @deprecated use IAuthTokenService.getIdentificationHeaders()
   */
  getDefaultHeadersOptions(): Record<string, string>;

  /**
   * Gets the default header options.
   *
   * @deprecated use IAuthTokenService.getIdentificationHeaders()
   */
  getIdentificationHeaders(information?: Required<IApiEndpointInformation>): Headers;

  /**
   * Refresh the OAuth token.
   *
   * @deprecated use IAuthService.refreshToken()
   */
  refreshToken(): Observable<void>;

  /**
   * Refresh the OAuth token in the background.
   *
   * @deprecated use IAuthService.refreshToken()
   */
  refreshTokenInBackground(): void;
}

export const IMobizHttpService: InjectionToken<IMobizHttpService>
  = new InjectionToken<IMobizHttpService>('IMobizHttpService');
